import type { Asset, StockAsset, EtfAsset, FundAsset, CryptoAsset } from "@/schemas";
import { formatPrice, formatLargeCurrency } from "@/lib/formatters";
import { TrendingUp } from "lucide-react";

interface KeyMetricsProps {
  asset: Asset;
}

function MetricCard({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="p-3 rounded-lg bg-muted/30 border border-muted">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className="text-sm font-bold">{value}</p>
    </div>
  );
}

function StockEtfMetrics({ asset }: { asset: StockAsset | EtfAsset }) {
  return (
    <>
      {asset.market_cap && (
        <MetricCard
          label="Market Cap"
          value={formatLargeCurrency(asset.market_cap, asset.currency_symbol)}
        />
      )}
      {asset.trailing_pe && (
        <MetricCard label="P/E Ratio" value={asset.trailing_pe.toFixed(2)} />
      )}
      {asset.dividend_yield !== undefined && asset.dividend_yield !== null && (
        <MetricCard
          label="Div Yield"
          value={`${(asset.dividend_yield * 100).toFixed(2)}%`}
        />
      )}
      {asset.beta && <MetricCard label="Beta" value={asset.beta.toFixed(2)} />}
      {asset.fifty_two_week_high && (
        <MetricCard
          label="52W High"
          value={formatPrice(asset.fifty_two_week_high, asset.currency_symbol)}
        />
      )}
      {asset.fifty_two_week_low && (
        <MetricCard
          label="52W Low"
          value={formatPrice(asset.fifty_two_week_low, asset.currency_symbol)}
        />
      )}
      {asset.average_volume && (
        <MetricCard
          label="Avg Volume"
          value={`${(asset.average_volume / 1e6).toFixed(1)}M`}
        />
      )}
      {asset.price_to_book && (
        <MetricCard label="P/B Ratio" value={asset.price_to_book.toFixed(2)} />
      )}
    </>
  );
}

function CryptoMetrics({ asset }: { asset: CryptoAsset }) {
  return (
    <>
      {asset.market_cap && (
        <MetricCard
          label="Market Cap"
          value={formatLargeCurrency(asset.market_cap, asset.currency_symbol)}
        />
      )}
      {asset.circulating_supply && (
        <MetricCard
          label="Circ Supply"
          value={`${(asset.circulating_supply / 1e6).toFixed(1)}M`}
        />
      )}
      {asset.volume_24hr && (
        <MetricCard
          label="24h Volume"
          value={formatLargeCurrency(asset.volume_24hr, asset.currency_symbol)}
        />
      )}
      {asset.all_time_high && (
        <MetricCard
          label="ATH"
          value={formatPrice(asset.all_time_high, asset.currency_symbol)}
        />
      )}
    </>
  );
}

function FundMetrics({ asset }: { asset: FundAsset }) {
  return (
    <>
      {asset.net_assets && (
        <MetricCard
          label="Net Assets"
          value={formatLargeCurrency(asset.net_assets, asset.currency_symbol)}
        />
      )}
      {asset.expense_ratio !== undefined && asset.expense_ratio !== null && (
        <MetricCard
          label="Expense Ratio"
          value={`${(asset.expense_ratio * 100).toFixed(2)}%`}
        />
      )}
      {asset.yield !== undefined && asset.yield !== null && (
        <MetricCard
          label="Yield"
          value={`${(asset.yield * 100).toFixed(2)}%`}
        />
      )}
      {asset.nav_price && (
        <MetricCard
          label="NAV"
          value={formatPrice(asset.nav_price, asset.currency_symbol)}
        />
      )}
      {asset.category && <MetricCard label="Category" value={asset.category} />}
      {asset.fund_family && (
        <MetricCard label="Family" value={asset.fund_family} />
      )}
    </>
  );
}

export function KeyMetrics({ asset }: KeyMetricsProps) {
  const isStockOrEtf = asset.type === "stock" || asset.type === "etf";
  const isFund = asset.type === "fund";
  const isCrypto = asset.type === "crypto";

  if (!isStockOrEtf && !isCrypto && !isFund) {
    return null;
  }

  return (
    <div>
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-primary" />
        Key Metrics
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {isStockOrEtf && (
          <StockEtfMetrics asset={asset as StockAsset | EtfAsset} />
        )}
        {isFund && <FundMetrics asset={asset as FundAsset} />}
        {isCrypto && <CryptoMetrics asset={asset as CryptoAsset} />}
      </div>
    </div>
  );
}
